import { Button, CharField, Flex, H1, Tabs, useBreadcrumbs } from "@inmagik/magik-ui"
import Layout from "../../components/Layout"
import { useNavigate, useParams } from "react-router-dom"
import { Formik } from "formik"
import { Container } from "react-bootstrap"
import BottomFormFixed from "../../components/BottomFormFixed"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useMemo, useState } from "react"
import ModalDelete from "../../components/ModalDelete"
import { ScrollLayout } from "../../components/ScrollLayout"
import type { FiltersBase, Resource } from "../../types"
import { initialValuesResource, validationSchemaResource } from "./ResourceCommon"
import ResourceForm from "./ResourceForm"
import { useQsFilters } from "../../hooks/filters"
import MediaDocumentModal from "../Documents/MediaDocumentModal"
import { useCreateMediaResource, useDeleteResource, useMediaListResource, useResource, useUpdateResource } from "../../hooks/resources"
import { TableMedia } from "./TableMedia"

const initFilters = (params: URLSearchParams) =>
  ({
    page: Number(params.get("page") ?? 1),
    search: params.get("search") ?? "",
    ordering: params.get("ordering") ?? "",
  } as FiltersBase)

export default function ResourceUpdate() {
  const updateResource = useUpdateResource()
  const { filters, uiFilters, setFilters } = useQsFilters(initFilters)
  const navigate = useNavigate()

  const { id } = useParams()

  const { data: resource } = useResource(Number(id))

  const { data: mediaList, refetch } = useMediaListResource(Number(id))

  const deleteResource = useDeleteResource()

  const [modalStateDeleteResource, modalActionsDeleteResource] = useModalTrigger()
  const [modalImmagineCreateState, modalImmagineCreateActions] = useModalTrigger()

  const [activeTab, setActiveTab] = useState("dettaglio")

  const initialValues = useMemo(() => initialValuesResource(resource), [resource])
  const validationSchema = useMemo(() => validationSchemaResource(), [])

  const createMediaResource = useCreateMediaResource(Number(id))

  useBreadcrumbs([{ label: "Risorse", href: "/resources/resources" }, { label: "Dettaglio Risorsa" }])

  return (
    <Layout>
      <ScrollLayout hasBottomFixed>
        <Flex alignItems="center" justifyContent="between">
          <H1>{resource?.title}</H1>
          {activeTab === "dettaglio" && (
            <Button
              size="md"
              title="Elimina"
              variant="danger"
              leadingIcon="delete"
              onClick={() => {
                modalActionsDeleteResource.open()
              }}
            />
          )}
          {activeTab === "immagini" && (
            <Button
              size="md"
              className="text-white"
              title="Aggiungi"
              leadingIcon="add"
              onClick={() => {
                modalImmagineCreateActions.open({
                  order: mediaList ? mediaList.length + 1 : 1,
                })
              }}
            />
          )}
        </Flex>
        <ScrollLayout.Tabs
          className="mt-6"
          onTabChange={(tab) => {
            setActiveTab(tab)
          }}
          initialTab="dettaglio"
        >
          <ScrollLayout.Tab id="dettaglio" label="Dettaglio" key={"dettaglio"}>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={(values) => {
                return updateResource.mutateAsync(values as Resource).then(() => {
                  navigate(`/resources/resources`)
                })
              }}
            >
              {({ handleSubmit }) => (
                <form onSubmit={handleSubmit}>
                  <Container fluid className="mt-6" style={{ marginBottom: "100px" }}>
                    <ResourceForm />
                  </Container>
                  <BottomFormFixed url="/resources/resources" />
                </form>
              )}
            </Formik>
          </ScrollLayout.Tab>
          <Tabs.Tab id="immagini" label="Media" key={"immagini"}>
            <Flex className="mt-3">
              <CharField
                label=""
                placeholder="Cerca..."
                leadingIcon="search"
                style={{
                  width: 300,
                }}
                value={filters.search}
                onChange={(value) => {
                  setFilters({ ...filters, search: value, page: 1 })
                }}
              />
            </Flex>
            <div className="mt-3">
              <TableMedia
                refetch={refetch}
                resourceMedia={mediaList || []}
                setFilters={setFilters}
                filters={uiFilters}
                recordId={Number(id)}
              />
            </div>
          </Tabs.Tab>
        </ScrollLayout.Tabs>
      </ScrollLayout>
      <ModalDelete
        isOpen={modalStateDeleteResource.isOpen}
        onClose={modalActionsDeleteResource.close}
        onDelete={() => {
          return deleteResource.mutateAsync(resource?.id).then(() => {
            navigate(`/resources/resources`)
          })
        }}
        message="Sei sicuro di voler eliminare questa Risorsa?"
        title="Elimina Risorsa"
      />
      {modalImmagineCreateState.isOpen && (
        <MediaDocumentModal
          title="Aggiungi Media"
          id={resource?.id}
          show={modalImmagineCreateState.isOpen}
          onClose={modalImmagineCreateActions.close}
          save={async (values) => {
            const result = await createMediaResource.mutateAsync(values)
            if (result) {
              modalImmagineCreateActions.close()
              refetch()
            }
          }}
        />
      )}
    </Layout>
  )
}
